import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { StyledToolbarItem, StyledToolbarButton, StyledToolbarMenu, StyledToolbarDialog } from './ToolbarItem.styles';
import { ToolbarItemProps, ToolbarItemMode } from './ToolbarItem.types';

export const ToolbarItem: React.FC<ToolbarItemProps> = props => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const openPane = () => {
    setOpen(true);
    if (props.mode === ToolbarItemMode.PANEL) props.onOpen?.();
  };

  const close = () => {
    setOpen(false);
    if (props.mode === ToolbarItemMode.PANEL) props.onClose?.();
  };

  const toggle = () => (open ? close() : openPane());

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      close();
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !popRef.current?.contains(t)) {
        close();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [open]);

  return (
    <StyledToolbarItem className={props.className}>
      <StyledToolbarButton
        ref={triggerRef}
        type="button"
        aria-haspopup={props.mode === ToolbarItemMode.DROPDOWN ? 'menu' : 'dialog'}
        aria-expanded={open}
        aria-label={props.ariaLabel}
        onClick={toggle}
      >
        {props.icon && <span className="toolbar-btn-icon">{props.icon}</span>}
        {props.label && <span className="toolbar-btn-label">{props.label}</span>}
        <span className="toolbar-btn-Chevron icon-arrow-down" aria-hidden></span>
      </StyledToolbarButton>

      {open && props.mode === ToolbarItemMode.DROPDOWN && (
        <StyledToolbarMenu ref={popRef} role="menu" aria-label={props.label}>
          {props.items?.map(item => {
            const selected = props.selectedKey === item.key;
            return (
              <button
                key={item.key}
                role="menuitemradio"
                aria-checked={selected}
                type="button"
                className={clsx('toolbar-menu-item', selected && 'is-selected')}
                onClick={() => {
                  props.onSelect?.(item.key);
                  close();
                }}
              >
                {item.icon && <span className="toolbar-menu-icon">{item.icon}</span>}
                <span className="toolbar-menu-label">{item.label}</span>
              </button>
            );
          })}
        </StyledToolbarMenu>
      )}

      {open && props.mode === ToolbarItemMode.PANEL && (
        <StyledToolbarDialog ref={popRef} role="dialog" aria-label={props.label} className={props.panelClassName}>
          {props.renderPanel?.(close)}
        </StyledToolbarDialog>
      )}
    </StyledToolbarItem>
  );
};
